import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup, ValidatorFn, AbstractControl } from '@angular/forms';
import { CountryISO } from 'ngx-intl-tel-input';
import { SharedService } from '../../../shared/shared.service';
import { API_MODULES } from '../../../core/api-modules';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-careers',
  templateUrl: './careers.component.html',
  styleUrls: ['./careers.component.scss']
})
export class CareersComponent implements OnInit {
  country: 'UAE' | 'OMAN' = 'UAE';
  omanForm: FormGroup;
  uaeForm: FormGroup;
  ocities = ['Muscat', 'Sohar', 'Salalah', 'Nizwa', 'Other'];
  selectedOCity = 'Muscat';

  ucities = [
    'Dubai',
    'Abu Dhabi',
    'Sharjah',
    'Ajman',
    'Ras Al Khaimah',
    'Fujairah',
    'Umm Al Quwain'
  ];
  selectedUCity = 'Dubai';

  // Dropdown Values Based on Country
  departments = {
    OMAN: [
      "Sales", "Logistics & Supply Chain", "Engineering", "Human Resources",
      "Finance", "Admin", "Public Relation", "Auditing", "Wearhouse",
      "Security", "Management"
    ],
    UAE: [
      "Sales", "Logistics & Supply Chain", "Engineering", "Human Resources",
      "Facilities Management", "Finance", "Export", "Warehouse", "IT",
      "Designing", "Marketing", "Production", "R&D - QA", "Auto Fleet", "HSE"
    ]
  };

  educationList = [
    "High School",
    "Diploma",
    "Bachelor's Degree",
  ];

  drivingOm = [
    "Manual Light",
    "Auto Light",
    "Heavy",
    "Light Bus",
    "Heavy Bus",
    "Forklift"
  ];

  drivingUae = [
    "Light Motor Vehicle - Automatic",
    "Light Motor Vehicle - Manual",
    "Heavy Truck"
  ];

  healthOptions = ["Yes", "No"];
  relativeOptions = ["Yes", "No"];
  nocOptions = ["Yes", "No"];

  CountryISO = CountryISO;

  /** Global min/max length for national number (across all countries) - for template */
  PHONE_NATIONAL_MIN = CareersComponent.PHONE_NATIONAL_MIN;
  PHONE_NATIONAL_MAX = CareersComponent.PHONE_NATIONAL_MAX;
  static readonly PHONE_NATIONAL_MIN = 6;
  static readonly PHONE_NATIONAL_MAX = 15;

  submitting = false;
  submitError: string | null = null;
  lastRegNo: string | null = null;
  private selectedCvFile: File | null = null;

  constructor(
    private fb: FormBuilder,
    private api: SharedService
  ) {
    // OMAN Form - default country Oman
    this.omanForm = this.fb.group({
      country: ['OMAN', Validators.required],
      city: ['', Validators.required],
      department: ['', Validators.required],
      name: ['', Validators.required],
      nationality: ['', Validators.required],
      age: ['', Validators.required],
      gender: ['', Validators.required],
      dob: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      mobile1: ['', [Validators.required, this.phoneLengthValidator()]],
      mobile2: ['', [this.phoneLengthValidator()]],
      currentCountry: [''],
      currentCity: [''],
      employer: [''],
      position: [''],
      experience: ['', Validators.required],
      cv: ['', Validators.required],
      education: ['', Validators.required],
      driving: ['', Validators.required],
      health: ['', Validators.required],
      relative: ['', Validators.required],
      noc: ['', Validators.required] // Required for OMAN
    });

    // UAE Form - default country UAE
    this.uaeForm = this.fb.group({
      country: ['UAE', Validators.required],
      city: ['', Validators.required],
      department: ['', Validators.required],
      name: ['', Validators.required],
      nationality: ['', Validators.required],
      age: ['', Validators.required],
      gender: ['', Validators.required],
      dob: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      mobile1: ['', [Validators.required, this.phoneLengthValidator()]],
      mobile2: ['', [this.phoneLengthValidator()]],
      currentCountry: [''],
      currentCity: [''],
      employer: [''],
      position: [''],
      experience: ['', Validators.required],
      cv: ['', Validators.required],
      education: ['', Validators.required],
      driving: ['', Validators.required],
      health: ['', Validators.required],
      relative: ['', Validators.required],
      noc: [''] // Not required for UAE
    });
  }

  ngOnInit(): void {
    this.country = 'UAE';
  }

  /** Validator: national number length between global min and max (all countries). */
  private phoneLengthValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: unknown } | null => {
      const v = control.value;
      if (!v || typeof v !== 'object' || v.nationalNumber == null) return null;
      const len = String(v.nationalNumber).replace(/\D/g, '').length;
      if (len > 0 && (len < CareersComponent.PHONE_NATIONAL_MIN || len > CareersComponent.PHONE_NATIONAL_MAX)) {
        return {
          phoneLength: {
            min: CareersComponent.PHONE_NATIONAL_MIN,
            max: CareersComponent.PHONE_NATIONAL_MAX,
            actual: len
          }
        };
      }
      return null;
    };
  }

  changeCountry(val: 'OMAN' | 'UAE'): void {
    this.country = val;
  
    setTimeout(() => {
      const dialCode = val === 'OMAN' ? '+968' : '+971';
      ['mobile1', 'mobile2'].forEach(field => {
        const input: HTMLInputElement | null = document.querySelector(
          `#career-${field}-${this.country} input.iti__tel-input`
        );
        if (input) {
          input.placeholder = dialCode;
        }
      });
    }, 100);
  }
  

  updatePhonePlaceholder(event: any, field: string) {
    const dialCode = event.dialCode ? `+${event.dialCode}` : '';
  
    setTimeout(() => {
      const input: HTMLInputElement | null = document.querySelector(
        `#career-${field}-${this.country} input.iti__tel-input`
      );
      if (input) {
        input.placeholder = dialCode;
      }
    }, 50);
  }
  

  getCurrentForm(): FormGroup {
    return this.country === 'OMAN' ? this.omanForm : this.uaeForm;
  }

  get defaultPhoneCountry(): CountryISO {
    return this.country === 'OMAN' ? CountryISO.Oman : CountryISO.UnitedArabEmirates;
  }

  get preferredPhoneCountries(): CountryISO[] {
    return this.country === 'OMAN'
      ? [CountryISO.Oman, CountryISO.UnitedArabEmirates]
      : [CountryISO.UnitedArabEmirates, CountryISO.Oman];
  }

  private toPhoneString(value: any): string {
    if (!value || typeof value !== 'object') return '';
    return (
      (value.internationalNumber as string) ||
      (value.e164Number as string) ||
      (value.nationalNumber != null ? String(value.nationalNumber) : '')
    );
  }

  private buildPayload(isUae: boolean, form: FormGroup): any {
    const v = form.value;

    const base = {
      name: (v.name || '').trim(),
      email: (v.email || '').trim(),
      mobile_number: this.toPhoneString(v.mobile1),
      city: v.city || '',
      preferred_department: v.department || '',
      nationality: v.nationality || '',
      age: v.age ?? null,
      gender: v.gender || '',
      date_of_birth: v.dob || '',
      mobile_number_alt: this.toPhoneString(v.mobile2),
      current_country: v.currentCountry || '',
      current_city: v.currentCity || '',
      current_employer: v.employer || '',
      current_position: v.position || '',
      total_experience: v.experience || '',
      education: v.education || '',
      driving_license: v.driving || '',
      health_issues: v.health || '',
      relative_working_unikai: v.relative || ''
    };

    if (isUae) {
      return {
        ...base,
        country: 'UAE',
        completed_six_months_current_uae_employer: v.noc || ''
      };
    } else {
      return {
        ...base,
        country: 'Oman',
        can_provide_new_noc_from_rop: v.noc || ''
      };
    }
  }

  async onSubmit(): Promise<void> {
    const form = this.getCurrentForm();
    if (!form.valid) {
      form.markAllAsTouched();
      return;
    }

    const isUae = this.country === 'UAE';
    const module = isUae ? API_MODULES.CAREERS_UAE : API_MODULES.CAREERS_OMAN;
    const payload = this.buildPayload(isUae, form);

    // If CV file is selected, send multipart/form-data with file + fields
    let body: any = payload;
    if (this.selectedCvFile) {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        formData.append(key, typeof value === 'number' ? String(value) : (value as string));
      });
      formData.append('cv_file', this.selectedCvFile, this.selectedCvFile.name);
      body = formData;
    }

    this.submitting = true;
    this.submitError = null;
    this.lastRegNo = null;

    try {
      const res: any = await this.api.create(module, body);
      const regNo = res?.data?.reg_no || res?.reg_no || null;

      await Swal.fire({
        icon: 'success',
        title: 'Application Submitted',
        html: regNo ? `Your application reference number is: <strong>${regNo}</strong>` : 'Your application has been submitted successfully.',
        confirmButtonColor: '#022a72'
      });

      this.clearForm();
    } catch (err: any) {
      this.submitError = err?.message || 'Failed to submit application. Please try again.';
    } finally {
      this.submitting = false;
    }
  }

  onCvChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    this.selectedCvFile = file;
  }

  private clearForm(): void {
    const form = this.getCurrentForm();
    const isUae = this.country === 'UAE';
    const defaults = isUae
      ? { country: 'UAE', city: '', department: '', name: '', nationality: '', age: '', gender: '', dob: '', email: '', mobile1: '', mobile2: '', currentCountry: '', currentCity: '', employer: '', position: '', experience: '', cv: null, education: '', driving: '', health: '', relative: '', noc: '' }
      : { country: 'OMAN', city: '', department: '', name: '', nationality: '', age: '', gender: '', dob: '', email: '', mobile1: '', mobile2: '', currentCountry: '', currentCity: '', employer: '', position: '', experience: '', cv: null, education: '', driving: '', health: '', relative: '', noc: '' };
    form.reset(defaults);
    form.markAsPristine();
    form.markAsUntouched();
    this.selectedCvFile = null;
    this.lastRegNo = null;
    const fileInput = document.querySelector('.career-container input[type="file"]') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }
}
